import type { ReactNode } from 'react';
import { useEffect } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { IconHtml } from '@hivebrite/design-system/Icon';
import { useTheme } from '@hivebrite/design-system/Theme';
import { Tooltip } from '@hivebrite/design-system/Tooltip';
import { useMeasure } from '@hivebrite/react-hooks';

import Drop from 'hb-builder/components/common/Drop';
import EmptyState from 'hb-builder/components/common/state/EmptyState';
import { Wrapper, WrapperBuilder } from 'hb-builder/components/common/Wrapper';
import {
  useComponentContext,
  useRendererContext,
  useSectionContext,
} from 'hb-builder/hooks';
import { getContent } from 'hb-builder/utils/item';
import IconExclamationMarkHollow from 'hb-react/shared/icons/ExclamationMarkHollow';
import mergeRefs from 'hb-react/shared/utils/mergeRefs';

import HtmlSettings from './HtmlSettings';

const componentType = 'single';

const Html = () => <div />; // never used

const RenderBuilder = ({ children }: { children?: ReactNode }) => {
  const isEmpty = !children;

  const { nbFinalChildren, sectionHeight } = useSectionContext();
  const { item } = useComponentContext();

  const [ref, { width: wrapperWidth }] = useMeasure();
  const isNarrow = wrapperWidth < 200;
  const attributes = item?.attributes;
  const text = __(
    'HTML code can be previewed by clicking on the “preview” button at the top right.',
  );

  return (
    <Drop accept={['single']}>
      {({ isOver, ref: dropRef }) => (
        // @ts-expect-error untyped
        <WrapperBuilder
          attributes={attributes}
          dataTestId="builder-component-html"
          isEmpty={isEmpty}
          ref={mergeRefs([ref, dropRef])}
          style={{}}
        >
          {() => (
            <StyledWrapper>
              {isEmpty ? (
                <EmptyHtmlSection
                  isOver={isOver}
                  isNarrow={isNarrow}
                  nbFinalChildren={nbFinalChildren}
                  sectionHeight={sectionHeight}
                />
              ) : (
                <NoPreviewHtmlSection isNarrow={isNarrow} text={text} />
              )}
            </StyledWrapper>
          )}
        </WrapperBuilder>
      )}
    </Drop>
  );
};

const EmptyHtmlSection = ({
  isOver,
  isNarrow,
  nbFinalChildren,
  sectionHeight,
}: {
  isOver: boolean;
  isNarrow: boolean;
  nbFinalChildren: number;
  sectionHeight?: number;
}) => {
  const padding = isNarrow ? 0 : 4;

  return (
    <EmptyState
      dataTestId="builder-component-empty-html"
      isOver={isOver}
      nbFinalChildren={nbFinalChildren}
      p={padding}
      sectionHeight={sectionHeight}
    >
      <Box
        alignItems="center"
        color="primary"
        flexDirection="column"
        textAlign="center"
      >
        <IconHtml size={80} />
        {!isNarrow && (
          <Box color="black">
            {__('Add HTML content on the floating panel')}
          </Box>
        )}
      </Box>
    </EmptyState>
  );
};

const NoPreviewHtmlSection = ({
  isNarrow,
  text,
}: {
  isNarrow: boolean;
  text: string;
}) => {
  const iconSize = isNarrow ? 80 : 40;
  const theme = useTheme();

  return (
    <Box
      data-testid="builder-component-no-preview-html"
      height="100%"
      p={2}
      width="100%"
    >
      <NoPreviewWrapper>
        <IconWrapper color={theme.colors.white} isNarrow={isNarrow}>
          <IconHtml size={iconSize} />
        </IconWrapper>
        <HtmlTooltip isNarrow={isNarrow} text={text} />
      </NoPreviewWrapper>
    </Box>
  );
};

const HtmlTooltip = ({
  isNarrow,
  text,
}: {
  isNarrow: boolean;
  text: string;
}) => {
  if (!isNarrow) {
    return <Text>{text}</Text>;
  }

  return (
    <Tooltip childrenElement={text}>
      <IconExclamationMarkHollow color="white" width={16} height={16} />
    </Tooltip>
  );
};

const Render = ({ children }) => {
  const content = getContent(children);
  const { isMobilePreview } = useRendererContext();

  useEffect(() => {
    const getScripts = (startIndex = 0) => {
      const start = content.indexOf('<script>', startIndex);

      if (start > -1) {
        const end = content.indexOf('</script>', start) + '</script>'.length;
        const script = content.substr(start, end - start);
        const rawScript = /<script>([\s\S]*?)<\/script>/.exec(script)?.[1];

        if (!rawScript) return;

        // TODO: Improve this part
        let executeScript;

        try {
          // eslint-disable-next-line no-new-func
          executeScript = new Function(rawScript);
          // eslint-disable-next-line no-empty
        } catch {}

        executeScript?.();

        if (end <= content.length - 1) {
          getScripts(end);
        }
      }
    };

    getScripts();
  }, [content]);

  return (
    // @ts-expect-error untyped
    <Wrapper
      attributes={{}}
      className=""
      componentType={componentType}
      dataTestId="builder-component-html"
      id=""
      isMobilePreview={isMobilePreview}
      style={{}}
    >
      <StyledWrapper>
        <WrapperHtml dangerouslySetInnerHTML={{ __html: content }} />
      </StyledWrapper>
    </Wrapper>
  );
};

Html.componentName = 'Html';
Html.componentType = componentType;
Html.componentLabel = __('Html');
Html.componentCategory = 'single';
Html.componentIcon = () => IconHtml;

Html.canBeDoubleClicked = true;
Html.canBeDragged = true;
Html.canBeListed = true;
Html.canBeSelected = true;

Html.item = {
  category: Html.componentCategory,
  name: Html.componentName,
  type: Html.componentName,
};

Html.toolbar = ['edit', 'drag', 'duplicate', 'delete'];

Html.Settings = HtmlSettings;
Html.SettingsTabs = [];

Html.RenderBuilder = RenderBuilder;
Html.Render = Render;

export default Html;

const IconWrapper = styled(Box).withConfig({
  shouldForwardProp: (prop) => !['isNarrow'].includes(prop),
})<{ isNarrow: boolean }>`
  ${({ isNarrow }) =>
    !isNarrow &&
    `
      left: 12px;
      position: absolute;
      top: 12px;
    `};
`;

const NoPreviewWrapper = styled(Box).attrs(() => ({
  alignItems: 'center',
  bg: '#383838',
  justifyContent: 'center',
  p: 4,
  width: '100%',
}))`
  position: relative;
  min-height: 150px;
`;

const Text = styled(Box).attrs(() => ({
  color: 'white',
  width: '50%',
}))`
  text-align: center;
`;

const WrapperHtml = styled.div`
  width: 100%;

  p {
    margin: unset;
  }
`;

const StyledWrapper = styled(Box)`
  flex: 1;
  line-height: 1.6;
  white-space: break-spaces;
  word-break: break-word;
`;
